import {
  Checkbox,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Radio,
  RadioChangeEvent,
  Row,
  Switch
} from 'antd'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import { FormInstance } from 'antd/lib'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { usePersonsServiceGetPersonByIdEndpoint } from 'src/api/queries'
import {
  BodyConstitutionChanges,
  Gender,
  Studies,
  TbPatientsContacts
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { Text } from 'src/components'
import { useLocales } from 'src/locales'

import { OpportunisticDiseaseInfiniteSelect } from '../../OpportunisticDiseaseInfiniteSelect'
import { useStyles } from '../styles'

type Props = {
  form: FormInstance
  isPrimary: boolean
}

const getAllergiesPrefix = (val: string) => {
  return ['form', 'anamnesisSection', 'allergies', val, 'isSelected']
}

const getPregnancyPrefix = (val: string) => {
  return ['form', 'anamnesisSection', 'pregnancy', val, 'isSelected']
}

export const ConsultationFormAnamnesisSection = ({
  form,
  isPrimary
}: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const { personId } = useParams()

  const [isOtherVaccines, setIsOtherVaccines] = useState(false)

  const { data: person } = usePersonsServiceGetPersonByIdEndpoint(
    { id: String(personId) },
    [personId],
    {
      enabled: !!personId
    }
  )

  const hasMedicinesAllergies = Form.useWatch(
    getAllergiesPrefix('medicineAllergies'),
    form
  )

  const hasFoodAllergies = Form.useWatch(
    getAllergiesPrefix('foodAllergies'),
    form
  )

  const hasOtherAllergies = Form.useWatch(
    getAllergiesPrefix('otherAllergies'),
    form
  )

  const hasPreviousPregnancy = Form.useWatch(
    getPregnancyPrefix('previous'),
    form
  )

  const hasPregnancySelected = Form.useWatch(
    ['form', 'anamnesisSection', 'hasPregnancySelected'],
    form
  )

  const hasCurrentPregnancy = Form.useWatch(getPregnancyPrefix('current'), form)

  const onVaccinesChange = (e: RadioChangeEvent) => {
    const value = e.target.value

    const isNationalCalendar = value === 'nationalCalendar'

    setIsOtherVaccines(!isNationalCalendar)

    form.setFieldsValue({
      form: {
        anamnesisSection: {
          vaccines: {
            nationalCalendar: isNationalCalendar,
            other: {
              isSelected: !isNationalCalendar,
              value: undefined
            }
          }
        }
      }
    })
  }

  const onAllergiesChange = (e: CheckboxChangeEvent, field: string) => {
    const isSelected = e.target.checked

    form.setFieldsValue({
      form: {
        anamnesisSection: {
          allergies: {
            [field]: {
              isSelected: isSelected,
              value: undefined
            }
          }
        }
      }
    })
  }

  return (
    <>
      <Col span={24}>
        <Text size="xl">{t('consultation_form.anamnesis_section')}</Text>
      </Col>

      <Col span={24}>
        <Form.Item
          name={['form', 'anamnesisSection', 'coveredDiseasesAndIllnessesIds']}
          label={`1. ${t('consultation_form.covered_diseases')}`}
        >
          <OpportunisticDiseaseInfiniteSelect
            fieldName={[
              'form',
              'anamnesisSection',
              'coveredDiseasesAndIllnessesIds'
            ]}
            form={form}
            placeholder={t('common.select_from_list')}
          />
        </Form.Item>
      </Col>

      <Divider className={styles.divider} />

      <Col span={24}>
        <Form.Item
          name={['form', 'anamnesisSection', 'surgeriesInAnamnesis']}
          label={`2. ${t('consultation_form.surgeries_in_anamnesis')}`}
        >
          <Input.TextArea
            placeholder={t('consultation_form.surgeries_in_anamnesis')}
          />
        </Form.Item>
      </Col>

      <Divider className={styles.divider} />

      {isPrimary && (
        <>
          <Col span={24}>
            <Form.Item
              name={['form', 'anamnesisSection', 'itsInAnamnesis']}
              label={`3. ${t('consultation_form.its_in_anamnesis')}`}
              rules={requiredRules}
            >
              <Radio.Group>
                <Radio value="syphilis">
                  {t('consultation_form.syphilis')}
                </Radio>
                <Radio value="noSpecification">
                  {t('consultation_form.no_specification')}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Divider className={styles.divider} />

          <Col span={12}>
            <Form.Item
              label={`4. ${t('consultation_form.vaccines')}`}
              rules={requiredRules}
            >
              <Radio.Group onChange={onVaccinesChange}>
                <Radio value="nationalCalendar">
                  {t('consultation_form.national_calendar')}
                </Radio>

                <Radio value="other">
                  {t('consultation_form.vaccines_other')}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          {isOtherVaccines && (
            <Col span={12}>
              <Form.Item
                name={[
                  'form',
                  'anamnesisSection',
                  'vaccines',
                  'other',
                  'value'
                ]}
                label={<></>}
              >
                <Input.TextArea
                  placeholder={t('consultation_form.vaccines_other')}
                />
              </Form.Item>
            </Col>
          )}

          <Divider className={styles.divider} />
        </>
      )}

      <Col span={24}>
        <Flex vertical gap={12}>
          <Text>
            {isPrimary ? '5.' : '3.'}{' '}
            {t('consultation_form.allergies.allergies')}
          </Text>

          <Flex gap={24}>
            <Form.Item
              name={[
                'form',
                'anamnesisSection',
                'allergies',
                'medicineAllergies',
                'isSelected'
              ]}
              className="full-width"
              valuePropName="checked"
            >
              <Checkbox
                onChange={(e) => onAllergiesChange(e, 'medicineAllergies')}
              >
                {t('consultation_form.allergies.medicine_allergies')}
              </Checkbox>
            </Form.Item>

            {hasMedicinesAllergies && (
              <Form.Item
                name={[
                  'form',
                  'anamnesisSection',
                  'allergies',
                  'medicineAllergies',
                  'value'
                ]}
                className="full-width"
              >
                <Input.TextArea
                  placeholder={t(
                    'consultation_form.allergies.medicine_allergies'
                  )}
                />
              </Form.Item>
            )}
          </Flex>

          <Flex gap={24}>
            <Form.Item
              className="full-width"
              name={[
                'form',
                'anamnesisSection',
                'allergies',
                'foodAllergies',
                'isSelected'
              ]}
              valuePropName="checked"
            >
              <Checkbox onChange={(e) => onAllergiesChange(e, 'foodAllergies')}>
                {t('consultation_form.allergies.food_allergies')}
              </Checkbox>
            </Form.Item>

            {hasFoodAllergies && (
              <Form.Item
                name={[
                  'form',
                  'anamnesisSection',
                  'allergies',
                  'foodAllergies',
                  'value'
                ]}
                className="full-width"
              >
                <Input.TextArea
                  placeholder={t('consultation_form.allergies.food_allergies')}
                />
              </Form.Item>
            )}
          </Flex>

          <Flex gap={24}>
            <Form.Item
              className="full-width"
              name={[
                'form',
                'anamnesisSection',
                'allergies',
                'otherAllergies',
                'isSelected'
              ]}
              valuePropName="checked"
            >
              <Checkbox
                onChange={(e) => onAllergiesChange(e, 'otherAllergies')}
              >
                {t('consultation_form.allergies.other_allergies')}
              </Checkbox>
            </Form.Item>

            {hasOtherAllergies && (
              <Form.Item
                name={[
                  'form',
                  'anamnesisSection',
                  'allergies',
                  'otherAllergies',
                  'value'
                ]}
                className="full-width"
              >
                <Input.TextArea
                  placeholder={t('consultation_form.allergies.other_allergies')}
                />
              </Form.Item>
            )}
          </Flex>
        </Flex>
      </Col>

      <Divider className={styles.divider} />

      <Col span={24}>
        <Form.Item
          name={['form', 'anamnesisSection', 'bodyConstitutionChanges']}
          label={`${isPrimary ? '6.' : '4.'} ${t('consultation_form.body_constitution_changes.title')}`}
          rules={requiredRules}
        >
          <Radio.Group>
            {Object.values(BodyConstitutionChanges).map((item) => (
              <Radio value={item} key={item}>
                {t(`consultation_form.body_constitution_changes.${item}`)}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      </Col>

      <Divider className={styles.divider} />

      <Col span={24}>
        <Form.Item
          name={['form', 'anamnesisSection', 'currentMedication']}
          label={`${isPrimary ? '7.' : '5.'} ${t('consultation_form.current_medication')}`}
        >
          <Input.TextArea placeholder={t('consultation_form.to_specify')} />
        </Form.Item>
      </Col>

      <Divider className={styles.divider} />

      {isPrimary && (
        <>
          <Col span={24}>
            <Text>
              8. {t('consultation_form.family_medical_anamnesis.title')}
            </Text>

            <Row gutter={[12, 12]}>
              <Col span={6}>
                <Form.Item
                  name={[
                    'form',
                    'anamnesisSection',
                    'familyMedicalAnamnesis',
                    'diabetesMellitus'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>
                    {t(
                      'consultation_form.family_medical_anamnesis.diabetesMellitus'
                    )}
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item
                  name={[
                    'form',
                    'anamnesisSection',
                    'familyMedicalAnamnesis',
                    'hypertension'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>
                    {t(
                      'consultation_form.family_medical_anamnesis.hypertension'
                    )}
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item
                  name={[
                    'form',
                    'anamnesisSection',
                    'familyMedicalAnamnesis',
                    'skinLesions'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>
                    {t(
                      'consultation_form.family_medical_anamnesis.skinLesions'
                    )}
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item
                  name={[
                    'form',
                    'anamnesisSection',
                    'familyMedicalAnamnesis',
                    'malignantFormations'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>
                    {t(
                      'consultation_form.family_medical_anamnesis.malignantFormations'
                    )}
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Divider className={styles.divider} />
        </>
      )}

      <Col span={24}>
        <Form.Item
          name={['form', 'anamnesisSection', 'tbPatientsContacts']}
          label={`${isPrimary ? '9.' : '6.'} ${t('consultation_form.tb_patients_contacts')}`}
          rules={requiredRules}
        >
          <Radio.Group>
            {Object.values(TbPatientsContacts).map((item) => (
              <Radio key={item} value={item}>
                {t(`epidemiology.symptoms.general.${item}`)}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      </Col>

      <Divider className={styles.divider} />

      {isPrimary && (
        <>
          <Col span={24}>
            <Text>
              10. {t('consultation_form.substance_consumption.title')}
            </Text>

            <Row gutter={[12, 12]}>
              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'anamnesisSection',
                    'substanceConsumption',
                    'drugs'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>
                    {t('consultation_form.substance_consumption.drugs')}
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'anamnesisSection',
                    'substanceConsumption',
                    'alcohol'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>
                    {t('consultation_form.substance_consumption.alcohol')}
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'anamnesisSection',
                    'substanceConsumption',
                    'smoking'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>
                    {t('consultation_form.substance_consumption.smoking')}
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Divider className={styles.divider} />
        </>
      )}

      <Col span={24}>
        <Text>
          {isPrimary ? '11.' : '7.'}{' '}
          {t('consultation_form.reproductive_and_sexual_health.title')}
        </Text>

        <Row gutter={[12, 12]}>
          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'anamnesisSection',
                'reproductiveAndSexualHealth',
                'contraception'
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                {t(
                  'consultation_form.reproductive_and_sexual_health.contraception'
                )}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'anamnesisSection',
                'reproductiveAndSexualHealth',
                'yes'
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                {t('consultation_form.reproductive_and_sexual_health.yes')}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'anamnesisSection',
                'reproductiveAndSexualHealth',
                'no'
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                {t('consultation_form.reproductive_and_sexual_health.no')}
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Col>

      {person?.gender === Gender.FEMALE && (
        <>
          <Divider className={styles.divider} />

          <Col span={24}>
            <Flex vertical gap={12}>
              <Text>
                {isPrimary ? '12.' : '8.'}{' '}
                {t('consultation_form.pregnancy.title')}
              </Text>

              <Form.Item
                name={['form', 'anamnesisSection', 'hasPregnancySelected']}
                label={t('cases.pregnant')}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              {hasPregnancySelected && (
                <>
                  <Flex gap={24}>
                    <Form.Item
                      name={getPregnancyPrefix('previous')}
                      valuePropName="checked"
                      className="full-width"
                    >
                      <Checkbox>
                        {t('consultation_form.pregnancy.previous')}
                      </Checkbox>
                    </Form.Item>

                    {hasPreviousPregnancy && (
                      <Form.Item
                        name={[
                          'form',
                          'anamnesisSection',
                          'pregnancy',
                          'previous',
                          'value'
                        ]}
                        className="full-width"
                      >
                        <Input.TextArea
                          placeholder={t('consultation_form.to_specify')}
                        />
                      </Form.Item>
                    )}
                  </Flex>

                  <Flex gap={24}>
                    <Form.Item
                      name={getPregnancyPrefix('current')}
                      valuePropName="checked"
                      className="full-width"
                    >
                      <Checkbox>
                        {t('consultation_form.pregnancy.current')}
                      </Checkbox>
                    </Form.Item>

                    {hasCurrentPregnancy && (
                      <Form.Item
                        name={[
                          'form',
                          'anamnesisSection',
                          'pregnancy',
                          'current',
                          'value'
                        ]}
                        className="full-width"
                      >
                        <Input.TextArea
                          placeholder={t('consultation_form.to_specify')}
                        />
                      </Form.Item>
                    )}
                  </Flex>

                  <Form.Item
                    name={['form', 'anamnesisSection', 'pregnancy', 'planned']}
                    valuePropName="checked"
                  >
                    <Checkbox>
                      {t('consultation_form.pregnancy.planned')}
                    </Checkbox>
                  </Form.Item>
                </>
              )}
            </Flex>
          </Col>
        </>
      )}

      {isPrimary && (
        <>
          <Divider className={styles.divider} />

          <Col span={24}>
            <Flex vertical gap={16}>
              <Text>13. {t('consultation_form.social_anamnesis.title')}</Text>

              <Form.Item
                className="ml-16"
                name={[
                  'form',
                  'anamnesisSection',
                  'socialAnamnesis',
                  'studies'
                ]}
                label={`13.1 ${t('consultation_form.social_anamnesis.studies.title')}`}
              >
                <Radio.Group>
                  {Object.values(Studies).map((item) => (
                    <Radio value={item} key={item}>
                      {t(`consultation_form.social_anamnesis.studies.${item}`)}
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>

              <Form.Item
                className="ml-16"
                name={[
                  'form',
                  'anamnesisSection',
                  'socialAnamnesis',
                  'employmentAndOccupation'
                ]}
                label={`13.2 ${t('consultation_form.social_anamnesis.employment_and_occupation')}`}
              >
                <Input.TextArea
                  placeholder={t('consultation_form.to_specify')}
                />
              </Form.Item>
            </Flex>
          </Col>
        </>
      )}
    </>
  )
}
